import { useEffect, useState } from "react";
import { Instagram, ArrowUpRight, Play } from "lucide-react";
import { brand } from "../mock/content";
import { getActiveLang } from "../lib/activeLang";
import { INSTAGRAM_HANDLE, INSTAGRAM_URL, BEHOLD_FEED_ID } from "../lib/social";

const COPY = {
  it: { eyebrow: "Instagram", title: "Seguici su Instagram", follow: "Seguici" },
  en: { eyebrow: "Instagram", title: "Follow us on Instagram", follow: "Follow us" },
  de: { eyebrow: "Instagram", title: "Folge uns auf Instagram", follow: "Folgen" },
  fr: { eyebrow: "Instagram", title: "Suivez-nous sur Instagram", follow: "Suivez-nous" },
};

const postImage = (p) => p?.sizes?.medium?.mediaUrl || p?.sizes?.small?.mediaUrl || p?.thumbnailUrl || p?.mediaUrl;

export const InstagramFeed = ({ count = 6 }) => {
  const lang = getActiveLang();
  const c = COPY[lang] || COPY.it;
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    if (!BEHOLD_FEED_ID) return;
    const ctrl = new AbortController();
    fetch(`https://feeds.behold.so/${BEHOLD_FEED_ID}`, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then(async (d) => {
        let list = Array.isArray(d?.posts) ? d.posts.slice(0, count) : [];
        if (list.length && lang !== "it") {
          try {
            const API = process.env.REACT_APP_BACKEND_URL;
            const res = await fetch(`${API}/api/instagram/translate`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ texts: list.map((p) => p.prunedCaption || p.caption || ""), lang }),
              signal: ctrl.signal,
            });
            if (res.ok) {
              const tr = (await res.json()).translations || [];
              list = list.map((p, i) => ({ ...p, prunedCaption: tr[i] || p.prunedCaption }));
            }
          } catch (e) { /* mantieni originale */ }
        }
        setPosts(list);
      })
      .catch((e) => { if (e.name !== "AbortError") setPosts([]); });
    return () => ctrl.abort();
  }, [count, lang]);

  const showGrid = posts && posts.length > 0;

  return (
    <section className="bg-black text-white py-16 md:py-24 border-t border-white/10" data-testid="instagram-feed">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <span className="text-white/60 text-[12px] uppercase tracking-[0.28em]" style={{ fontFamily: "Space Grotesk" }}>{c.eyebrow}</span>
            <h2 className="mt-4 font-black leading-[0.98] tracking-tight" style={{ fontFamily: "Archivo", fontSize: "clamp(1.8rem,3.4vw,2.8rem)" }}>{c.title}</h2>
          </div>
          <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" data-testid="instagram-profile-link"
            className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em] text-white/70 hover:text-white transition-colors" style={{ fontFamily: "Space Grotesk" }}>
            @{INSTAGRAM_HANDLE} <ArrowUpRight size={18} />
          </a>
        </div>

        {showGrid ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="instagram-grid">
            {posts.map((p) => (
              <a key={p.id} href={p.permalink} target="_blank" rel="noreferrer" data-testid={`instagram-post-${p.id}`}
                className="group block">
                <div className="relative aspect-square overflow-hidden rounded-md bg-white/5">
                  <img src={postImage(p)} alt={p.prunedCaption || "Post Instagram TORRI"} loading="lazy" draggable={false}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  {p.mediaType === "VIDEO" && (
                    <span className="absolute top-2 right-2 text-white drop-shadow"><Play size={16} fill="currentColor" /></span>
                  )}
                  <span className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center">
                    <Instagram size={22} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </span>
                </div>
                {(p.prunedCaption || p.caption) && (
                  <p className="mt-2 text-white/55 text-xs leading-snug line-clamp-2 group-hover:text-white/80 transition-colors" style={{ fontFamily: "Inter" }}>
                    {p.prunedCaption || p.caption}
                  </p>
                )}
              </a>
            ))}
          </div>
        ) : (
          <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" data-testid="instagram-fallback"
            className="group flex flex-col items-center justify-center gap-4 min-h-[220px] border border-white/12 rounded-lg text-center px-6 py-12 transition-colors hover:border-white/30">
            <span className="w-14 h-14 flex items-center justify-center rounded-full transition-transform group-hover:scale-105" style={{ background: brand.yellow }}>
              <Instagram size={26} className="text-black" />
            </span>
            <span className="text-lg font-semibold" style={{ fontFamily: "Archivo" }}>@{INSTAGRAM_HANDLE}</span>
            <span className="inline-flex items-center gap-2 px-6 py-3 text-black text-sm uppercase tracking-[0.12em] font-semibold" style={{ background: brand.yellow, fontFamily: "Space Grotesk" }}>
              {c.follow} <ArrowUpRight size={16} />
            </span>
          </a>
        )}
      </div>
    </section>
  );
};
